/* eslint-disable max-len */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export default class BitwiseNode extends BaseNode {
    operator: string;

    constructor() {
        super();
        this.linkedFeature = 'general';
        this.name = 'Bitwise';
        this.id = 'node_bitwise';

        this.inputs = [
            {
                name: 'Exec',
                portId: 'exec',
                params: [],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'OPERAND1',
                name: 'Operand 1',
                settingType: SettingType.STRINGVARIABLE,
                required: true,
                default: '',
            },
            {
                id: 'OPERATOR',
                name: 'Operator',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [
                    {
                        name: 'AND',
                        value: 'AND',
                    },
                    {
                        name: 'OR',
                        value: 'OR',
                    },
                    {
                        name: 'XOR',
                        value: 'XOR',
                    },
                    {
                        name: 'Left Shift (<<)',
                        value: 'SHL',
                    },
                    {
                        name: 'Right Shift (>>)',
                        value: 'SHR',
                    },
                ],
            },
            {
                id: 'OPERAND2',
                name: 'Operand 2',
                settingType: SettingType.STRINGVARIABLE,
                required: true,
                default: '',
            },
        ];

        this.outputs = [
            {
                name: 'Answer',
                portId: 'answer',
                params: [
                    {
                        name: 'result',
                        dataType: TibboDataType.INTEGER,
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project: ProjectConfiguration = this.project;
            const links = project.getNodeLinks(this, 'answer');
            if (links.length > 0) {
                const VARIABLE = this.getSettingValue('VARIABLE');
                let varName = '';
                if (VARIABLE !== '') {
                    const assignedVariable = project.getFeature(VARIABLE);
                    varName = `{{$${assignedVariable?.getSettingValue('ID')}}}`;
                } else {
                    varName = 'result';
                }
                const link = links[0];
                const op1 = this.getSettingValue('OPERAND1');
                const op2 = this.getSettingValue('OPERAND2');
                const OPERATOR = this.getSettingValue('OPERATOR');

                let operatorString = '';
                switch (OPERATOR) {
                    case 'AND':
                        operatorString = '&';
                        break;
                    case 'OR':
                        operatorString = '|';
                        break;
                    case 'XOR':
                        operatorString = '^';
                        break;
                    case 'SHL':
                        operatorString = '<<';
                        break;
                    case 'SHR':
                        operatorString = '>>';
                        break;
                    default:
                        operatorString = OPERATOR;
                        break;
                }
                return `${op1} ${operatorString} ${op2}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;

        const incomingLinks = project.getNodeLinks(this, 'exec');
        const outgoing = project.getNodeLinks(this, 'answer');
        const nodeID = this.getID();

        for (let i = 0; i < incomingLinks.length; i += 1) {
            const link = incomingLinks[i];

            const op1 = this.getSettingValue('OPERAND1');
            const op2 = this.getSettingValue('OPERAND2');
            let OPERATOR = this.getSettingValue('OPERATOR');
            if (project.runtime !== 'tios') {
                switch (OPERATOR) {
                    case 'AND':
                        OPERATOR = '&';
                        break;
                    case 'OR':
                        OPERATOR = '|';
                        break;
                    case 'XOR':
                        OPERATOR = '^';
                        break;
                    case 'SHL':
                        OPERATOR = '<<';
                        break;
                    case 'SHR':
                        OPERATOR = '>>';
                        break;
                    default:
                        OPERATOR = '';
                        break;
                }
            }

            const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
            const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;
            const op1Exp = project
                .parseCodeExpression(op1, sourceParams, TibboDataType.INTEGER);
            const op2Exp = project
                .parseCodeExpression(op2, sourceParams, TibboDataType.INTEGER);

            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `calc_${nodeID}(${op1Exp} ${OPERATOR} ${op2Exp})${project.runtime === 'zephyr' ? ';' : ''}`,
            });
        }

        let resultCode = '';

        if (outgoing.length > 0) {
            resultCode = `${project.getCodeLocationStart()}${outgoing[0].getSourceId()}${project.getCodeLocationEnd()}`;
        }


        if (project.runtime === 'tios') {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                id: `declare_calc_${nodeID}`,
                template: `
declare sub calc_${nodeID}(result as integer)
    `,
            });
            blocks.push({
                location: 'DEVICE_TBS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
sub calc_${nodeID}(result as integer)
    ${resultCode}
end sub
`,
            });
        }

        if (project.runtime === 'micropython') {
            blocks.push({
                location: 'FUNCTION_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
def calc_${nodeID}(result):
    ${resultCode}
    pass
`,
            });
        }

        if (project.runtime === 'zephyr') {
            blocks.push({
                location: 'FUNCTION_DECLARATIONS',
                blockType: BlockType.CODE,
                id: `declare_calc_${nodeID}`,
                template: `
void calc_${nodeID}(int result);`,
            });
            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
void calc_${nodeID}(int result)
{
    ${resultCode}
}
`,
            });
        }

        if (project.runtime === 'simulator') {
            blocks.push({
                location: 'FUNCTION_DEFINITIONS',
                blockType: BlockType.CODE,
                id: `calc_${nodeID}`,
                template: `
function calc_${nodeID}(result)
{
    ${resultCode}
}
`,
            });
        }

        return blocks;
    }
}
